import React from "react"
import { Link, graphql } from "gatsby"
import { Layout, Head as HeadComponent } from "@bw/layouts"
import { Button, Spacer, Section, CmsImage, Portrait } from "@bw/bits"
import { Cartouche } from "@bw/partials"
import { PillMarker } from "@bw/icons"
import { PageHeader } from "@bw/modules"
import { useTranslation } from "react-i18next"
import ProfileModel from "models/profile"
import { Content } from "@bw/modules/mapModules"
import { useBreadcrumbs } from "../hooks/useBreadcrumbs"

export const Head = ({ location, pageContext, data }) => (
  <HeadComponent
    title={data.page.metaTitle || data.page.title}
    image={data.page.image?.localImage?.childImageSharp?.original?.src}
    data={data}
    location={location}
    pageContext={pageContext}
    description={data.page.metaDescription}
    doNotIndex={data.page.doNotIndex}
  />
)

const ProfileTemplate = ({ data, pageContext, location }) => {
  const { page, parents, mount } = data
  const { t } = useTranslation()
  const Profile = new ProfileModel(page)
  const Pois = Profile.getPois()

  // generate breadcrumbs
  const profileParents =
    parents.nodes.length === 0
      ? Pois.length > 0
        ? [Pois[0]]
        : null
      : pageContext.parents.map(id =>
          parents.nodes.find(parent => parent.id === id)
        )

  const breadcrumbsLinks = useBreadcrumbs({
    mount,
    page,
    parents: profileParents,
  })

  let buttons = []

  if (page.phone) {
    buttons.push(
      <Button
        key="phone"
        label={page.phone}
        href={`tel:${page.phone}`}
        primary
      />
    )
  }

  if (page.booking_url) {
    buttons.push(
      <Button
        key="booking"
        label={t("profile.online_appointment")}
        href={page.booking_url}
        inversed
        external
      />
    )
  }

  if (page.personnal_url) {
    const url = page.personnal_url
    const external = page.personnal_url.startsWith("http")
    buttons.push(
      <Button
        key="personnal_url"
        label={page.personnal_url_label}
        href={external && url}
        to={!external && url}
        primary
        external={external}
      />
    )
  }

  return (
    <Layout {...{ pageContext, location }}>
      {/*
       * Loading the Contents modules, ex Content or CallToAction, TeasersLatest
       */}
      <PageHeader
        title={Profile.getFullName()}
        subtitle={page.role}
        breadcrumbs={breadcrumbsLinks}
        buttons={buttons}
        portrait={
          page.portrait ? (
            <Portrait
              image={<CmsImage src={page.portrait} alt={page.title} />}
            />
          ) : undefined
        }
      />
      {Pois.length > 0 && (
        <Section>
          <Cartouche>
            {Pois.map((Poi, i) => (
              <Link key={i} to={Poi.to} target="_blank" rel="noreferrer">
                <Spacer direction="row">
                  <PillMarker />
                  <div>
                    <div>
                      <b>{t("job.locationTitle")}</b>
                    </div>
                    {Poi.title}
                  </div>
                </Spacer>
              </Link>
            ))}
          </Cartouche>
        </Section>
      )}
      <Content modules={page.content} />
    </Layout>
  )
}

export default ProfileTemplate

export const profileQuery = graphql`
  query profileQuery($id: String!, $parents: [String]) {
    parents: allCollectionPages(filter: { id: { in: $parents } }) {
      nodes {
        id
        to: slug
        title
      }
    }

    page: collectionProfiles(id: { eq: $id }) {
      title
      titre {
        value
      }
      firstName
      role
      portrait {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 160)
          }
        }
      }
      displayFirstnameOnly
      phone
      booking_url
      personnal_url
      personnal_url_label
      metaTitle
      metaDescription
      image {
        localImage {
          childImageSharp {
            original {
              src
            }
          }
        }
      }
      doNotIndex

      poi {
        title
        to: url
      }

      content {
        __typename
        suptitle
        title
        subtitle
        backgroundVariant
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonOpenInModal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        idAttribute
        classAttribute

        ...AudioModuleFragment
        ...CallToActionModuleFragment
        ...ContentModuleFragment
        ...ContentWithImageModuleFragment
        ...GalleryModuleFragment
        ...ContentOnColumnsModuleFragment
        ...DownloadsModuleFragment
        ...TeasersManualModuleFragment
        ...LogoGridModuleFragment
        ...ImageModuleFragment
        ...VideoModuleFragment
        ...FormModuleFragment
        ...ListOfPoisModuleFragment
        ...ListOfArticlesModuleFragment
        ...ListOfProfilesModuleFragment
        ...ListOfJobsModuleFragment
        ...ListOfEventsModuleFragment
        ...LinksModuleFragment
        ...PredefinedModuleModuleFragment
      }
    }

    mount: collectionPages(mount: { handle: { eq: "profiles" } }) {
      slug
      title
    }
  }
`
